import React, { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

const AdSense = ({ 
  adClient = 'ca-pub-8226415393622981', // Use the hardcoded client ID
  adSlot, 
  adFormat = "auto",
  adLayout = "",
  adStyle = { display: 'block' },
  className = ""
}) => {
  const { isPremium, loading, userRole } = useAuth();
  const adRef = useRef(null);
  const isInitialized = useRef(false);
  const componentId = useRef(`ad-${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)}`);

  useEffect(() => {
    // Don't initialize ads if premium, loading, or already initialized
    if (loading || isPremium || isInitialized?.current || !adClient || !adSlot) {
      return;
    }

    // Wait for the AdSense script to load (it's already in index.html)
    const checkAndInitialize = () => {
      if (window.adsbygoogle && adRef?.current) {
        try {
          const adElement = adRef.current;
          if (!adElement?.hasAttribute('data-ad-status') && !adElement?.dataset?.adsbygoogle) {
            // Mark as initialized before calling push to prevent duplicate calls
            isInitialized.current = true;
            adElement.dataset.adsbygoogle = 'true';
            
            // Initialize AdSense for this specific element
            (window.adsbygoogle = window.adsbygoogle || []).push({});
          }
        } catch (error) {
          console.error('AdSense initialization error:', error);
          // Reset initialization flag on error so it can be retried
          isInitialized.current = false;
        }
      } else {
        // Retry after a short delay if script isn't ready
        setTimeout(checkAndInitialize, 100);
      }
    };

    // Start the initialization check
    checkAndInitialize();
  }, [loading, isPremium, adClient, adSlot]);

  // Reset initialization flag when component unmounts
  useEffect(() => {
    return () => {
      isInitialized.current = false;
    };
  }, []);

  // Don't render if user is premium or still loading auth
  if (loading) {
    return null; // Don't show ads while loading
  }

  if (isPremium) {
    return null; // Don't show ads for premium users
  }

  // Debug logging (remove in production)
  // console.log('AdSense render check:', { loading, isPremium, adClient, adSlot, userRole });

  // If no client ID or slot, don't render
  if (!adClient || !adSlot) {
    return null;
  }

  return (
    <div className={`${className} min-w-[300px]`}>
      <div className="text-xs text-muted-foreground text-center mb-0.5 opacity-60">
        Advertisement
      </div>
      <ins
        ref={adRef}
        className="adsbygoogle block"
        style={{
          display: 'block',
          minWidth: '300px',
          minHeight: '15px',
          maxHeight: '25px',
          ...adStyle
        }}
        data-ad-client={adClient}
        data-ad-slot={adSlot}
        data-ad-format={adFormat}
        data-ad-layout={adLayout}
        data-ad-responsive="true"
        key={componentId?.current}
      />
    </div>
  );
};

export default AdSense;